/**
 * SetorChipPicker — seletor multi-setor por chips agrupados em blocos.
 *
 * Regra visual:
 *  - Um chip por bloco (ex: "Bloco C"): clicar liga/desliga todos os setores do bloco.
 *  - Dentro do bloco expandido, chips individuais (C1, C2, …).
 *  - Individuais (sem número) ficam fora dos grupos.
 *  - Bloco parcialmente selecionado = chips individuais visíveis diretamente.
 */
import type { ReactNode } from "react";
import { agruparSetores } from "@/lib/setores";

interface SetorInfo { id: string; nome: string }

interface SetorChipPickerProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  setores: SetorInfo[];
  disabled?: boolean;
  /** Último chip da mesma linha (ex.: "Limpar" num filtro). Opcional. */
  fim?: ReactNode;
}

export function SetorChipPicker({ selectedIds, onChange, setores, disabled, fim }: SetorChipPickerProps) {
  const selectedSet = new Set(selectedIds);
  const { blocos, individuais } = agruparSetores(setores);
  const blocoKeys = Object.keys(blocos).sort();

  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const toggleBloco = (bloco: string) => {
    const ids = blocos[bloco].map(s => s.id);
    const allOn = ids.every(id => selectedSet.has(id));
    const next = new Set(selectedSet);
    if (allOn) {
      ids.forEach(id => next.delete(id));
    } else {
      ids.forEach(id => next.add(id));
    }
    onChange(Array.from(next));
  };

  const blocoState = (bloco: string): "all" | "some" | "none" => {
    const ids = blocos[bloco].map(s => s.id);
    const cnt = ids.filter(id => selectedSet.has(id)).length;
    if (cnt === ids.length) return "all";
    if (cnt > 0) return "some";
    return "none";
  };

  if (setores.length === 0) {
    return <p className="text-xs text-muted-foreground">Nenhum setor cadastrado</p>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {/* Blocos */}
      {blocoKeys.map(bloco => {
        const state = blocoState(bloco);
        const setoresBloco = blocos[bloco];
        const isAll = state === "all";
        const isSome = state === "some";

        return (
          <div key={bloco} className="flex flex-wrap gap-1">
            {/* Bloco chip */}
            <button
              type="button"
              disabled={disabled}
              onClick={() => toggleBloco(bloco)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                isAll
                  ? "bg-primary text-primary-foreground border-primary"
                  : isSome
                  ? "bg-sucesso-suave text-sucesso border-sucesso/30"
                  : "bg-card text-muted-foreground border-border hover:border-input"
              }`}
            >
              Bloco {bloco}
            </button>
            {/* Individual setor chips within bloco (always visible for granularity) */}
            {setoresBloco.map(s => {
              const on = selectedSet.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggle(s.id)}
                  className={`px-2 py-1 rounded-full text-xs border transition-colors disabled:opacity-50 ${
                    on
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-muted-foreground border-border hover:border-input"
                  }`}
                >
                  {s.nome}
                </button>
              );
            })}
          </div>
        );
      })}

      {/* Individuais */}
      {individuais.map(s => {
        const on = selectedSet.has(s.id);
        return (
          <button
            key={s.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(s.id)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
              on
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-muted-foreground border-border hover:border-input"
            }`}
          >
            {s.nome}
          </button>
        );
      })}

      {fim}
    </div>
  );
}
